/** Safe post-login return paths (open-redirect hardening + OAuth sessionStorage). */

const STORAGE_KEY = 'meisterpfad.postLoginRedirect';
const MAX_PATH_LENGTH = 2048;

const AUTH_PATHNAMES = new Set(['/login', '/register', '/pending-approval']);

function pathnameOf(path: string): string {
  const cut = path.search(/[?#]/);
  return cut === -1 ? path : path.slice(0, cut);
}

function isAuthRoute(pathname: string): boolean {
  if (AUTH_PATHNAMES.has(pathname)) return true;
  return pathname === '/auth' || pathname.startsWith('/auth/');
}

function readStorage(): Storage | null {
  try {
    return globalThis.sessionStorage;
  } catch {
    return null;
  }
}

/** Relative in-app path only. Rejects protocol-relative, auth, and overlong values. */
export function sanitizeReturnPath(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const path = raw.trim();
  if (!path || path.length > MAX_PATH_LENGTH) return null;
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return null;
  if (path.includes('\\') || path.includes('\0') || /[\r\n]/.test(path)) return null;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(path)) return null;
  if (path.includes('://')) return null;
  const pathname = pathnameOf(path);
  if (!pathname.startsWith('/') || pathname.startsWith('//')) return null;
  if (isAuthRoute(pathname)) return null;
  return path;
}

/** `/login?next=...` with the original location encoded. Empty/unsafe next → `/login`. */
export function loginPathWithNext(pathname: string, search = '', hash = ''): string {
  const next = sanitizeReturnPath(`${pathname}${search}${hash}`);
  if (!next) return '/login';
  return `/login?next=${encodeURIComponent(next)}`;
}

export function resolvePostLoginPath(instanceId: string, next?: string | null): string {
  const sanitized = sanitizeReturnPath(next);
  if (sanitized) return sanitized;
  const id = String(instanceId ?? '').trim();
  if (!id) return '/login';
  return `/i/${encodeURIComponent(id)}/dashboard`;
}

export function storeReturnPath(raw: string | null | undefined): void {
  const path = sanitizeReturnPath(raw);
  const storage = readStorage();
  if (!storage) return;
  try {
    if (path) storage.setItem(STORAGE_KEY, path);
    else storage.removeItem(STORAGE_KEY);
  } catch {
    // private mode / quota
  }
}

export function peekStoredReturnPath(): string | null {
  const storage = readStorage();
  if (!storage) return null;
  try {
    return sanitizeReturnPath(storage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

/** Read and clear the stored OAuth return path. */
export function takeStoredReturnPath(): string | null {
  const path = peekStoredReturnPath();
  const storage = readStorage();
  if (storage) {
    try {
      storage.removeItem(STORAGE_KEY);
    } catch {
      // noop
    }
  }
  return path;
}

/** Prefer `?next=` then sessionStorage; always clear storage so it cannot leak to a later login. */
export function takeReturnPath(queryNext?: string | null): string | null {
  const fromQuery = sanitizeReturnPath(queryNext);
  const fromStore = takeStoredReturnPath();
  return fromQuery ?? fromStore;
}
